//Forest Dash View
import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Forest, User, Tree, DailyExercise, ExerciseCompletion } from '../../types';
import { 
  getForestStudents, 
  getForestTrees, 
  updateStudentResources,
  getStudentExerciseCompletions,
  getForestExercises,
  getDailyExercisesByGroup,
  getUserWaterActions,
  getAllForestExerciseCompletions,
  getAllForestWaterActions,
  updateTreeGrowthStages,
} from '../../services/firestore';
import Button from '../common/Button';
import { ASSET_PATHS } from '../../utils/gameConfig';
import { activityManager } from '../../services/activityManager';

import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ForestDashboardViewProps {
  forest: Forest;
  onBack: () => void;
}

interface StudentWithStats extends User {
  treesPlanted: number;
  exercisesCompleted: number;
  lastActive?: Date;
  exerciseCompletions: ExerciseCompletion[];
  waterGiven: number;
  waterReceived: number;
}

interface ForestAnalytics {
  totalStudents: number;
  totalSeedsEarned: number;
  totalWaterEarned: number;
  totalSeedsPlanted: number;
  totalWaterSpent: number;
  waterSpentOnOthers: number;
  waterSpentOnSelf: number;
  percentageHelpingOthers: number;
  avgExercisesPerStudent: number;
  exerciseCompletionRate: number;
  mostActiveDays: number[];
  treeMaturityStats: { [stage: number]: number };
}

interface LeaderboardData {
  mostWaterEarned: StudentWithStats[];
  mostWaterShared: StudentWithStats[];
  mostTreesPlanted: StudentWithStats[];
  mostCollaborative: StudentWithStats[];
  topExerciseCompleters: StudentWithStats[];
}

const ForestDashboardView: React.FC<ForestDashboardViewProps> = ({
  forest,
  onBack
}) => {
  const [students, setStudents] = useState<StudentWithStats[]>([]);
  const [trees, setTrees] = useState<Tree[]>([]);
  const [exercises, setExercises] = useState<DailyExercise[]>([]);
  const [analytics, setAnalytics] = useState<ForestAnalytics | null>(null);
  const [leaderboards, setLeaderboards] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'students' | 'exercises' | 'analytics' | 'leaderboards'>('overview');
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [bulkResourceType, setBulkResourceType] = useState<'seeds' | 'water'>('seeds');
  const [bulkAmount, setBulkAmount] = useState<number>(1);

  //Activity monitor
  const [isInstructorActive, setIsInstructorActive] = useState(false);
  const [lastDataRefresh, setLastDataRefresh] = useState<Date | null>(null);
  const activityCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const dataRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  //Monitor setup
  useEffect(() => {
    setIsInstructorActive(activityManager.isActive());

    const checkActivity = () => {
      const currentlyActive = activityManager.isActive();
      const wasActive = isInstructorActive;

      if (currentlyActive !== wasActive) {
        setIsInstructorActive(currentlyActive);
        
        if (currentlyActive && !wasActive) {
          console.log('Instructor active, refreshing data');
          loadForestData();
        } else if (!currentlyActive && wasActive) {
          console.log('Instructor  inactive, pausing updates');
          //Stop auto-refresh
          if (dataRefreshIntervalRef.current) {
            clearInterval(dataRefreshIntervalRef.current);
            dataRefreshIntervalRef.current = null;
          }
        }
      }
    };

    activityCheckIntervalRef.current = setInterval(checkActivity, 30000);

    return () => {
      if (activityCheckIntervalRef.current) {
        clearInterval(activityCheckIntervalRef.current);
      }
      if (dataRefreshIntervalRef.current) {
        clearInterval(dataRefreshIntervalRef.current);
      }
    };
  }, [isInstructorActive]);

  //Initial load
  useEffect(() => {
    if (activityManager.isActive()) {
      loadForestData();
    } else {
      console.log('Instructor inactive, delaying forest data load');
      setIsLoading(false);
    }
  }, [forest.id]);

  //Refresh
  useEffect(() => {
    if (isInstructorActive && !dataRefreshIntervalRef.current) {
      console.log('Starting periodic data refresh for active instructor');
      dataRefreshIntervalRef.current = setInterval(() => {
        if (activityManager.isActive()) {
          console.log('Auto-refreshing forest data for active instructor');
          loadForestData();
        }
      }, 300000);
    } else if (!isInstructorActive && dataRefreshIntervalRef.current) {
      console.log('Stopping periodic data refresh for inactive instructor');
      clearInterval(dataRefreshIntervalRef.current);
      dataRefreshIntervalRef.current = null;
    }

    return () => {
      if (dataRefreshIntervalRef.current) {
        clearInterval(dataRefreshIntervalRef.current);
      }
    };
  }, [isInstructorActive]);



  //Data loading
  const loadForestData = async () => {
    //If active
    if (!activityManager.isActive()) {
      console.log('Instructor inactive, skipping forest data loading');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Loading forest data for active instructor:', forest.name);

      if (activityManager.isActive()) {
      await updateTreeGrowthStages(forest.id);
    }
      
      //Load students, trees, exercises
      const [
        forestStudents, 
        forestTrees, 
        forestExercises,
        allExerciseCompletions,
        allWaterActions
      ] = await Promise.all([
        getForestStudents(forest.id),
        getForestTrees(forest.id),
        getDailyExercisesByGroup(forest.dailyExerciseGroupId),
        getAllForestExerciseCompletions(forest.id),
        getAllForestWaterActions(forest.id)
      ]);

      //Process if active
      if (!activityManager.isActive()) {
        console.log('Instructor inactive (data loading), aborting');
        setIsLoading(false);
        return;
      }

      //Calculate student stats
      /*
      const studentsWithStats: StudentWithStats[] = await Promise.all(
        forestStudents.map(async (student) => {
          const [studentTrees, exerciseCompletions, waterActions] = await Promise.all([
            forestTrees.filter(tree => tree.planterId === student.uid),
            getStudentExerciseCompletions(forest.id, student.uid),
            getUserWaterActions(forest.id, student.uid)
          ]);

          const waterGiven = student.totalWaterUsedOnOthers;
          const waterReceived = waterActions.reduce((sum, action) => sum + action.amount, 0);

          return {
            ...student,
            treesPlanted: studentTrees.length,
            exercisesCompleted: exerciseCompletions.length,
            exerciseCompletions,
            waterGiven,
            waterReceived,
          };
        })
      );
      */


      const studentsWithStats: StudentWithStats[] = forestStudents.map(student => {
      const studentTrees = forestTrees.filter(tree => tree.planterId === student.uid);
      const exerciseCompletions = allExerciseCompletions.get(student.uid) || [];
      const waterActions = allWaterActions.get(student.uid) || [];
      
      const waterGiven = student.totalWaterUsedOnOthers;
      const waterReceived = waterActions.reduce((sum, action) => sum + action.amount, 0);

      return {
        ...student,
        treesPlanted: studentTrees.length,
        exercisesCompleted: exerciseCompletions.length,
        exerciseCompletions,
        waterGiven,
        waterReceived,
      };
    });


      //State
      if (!activityManager.isActive()) {
        console.log('Instructor inactive (during processing), discarding results');
        setIsLoading(false);
        return;
      }

      //Calculate analytics
      const analyticsData = calculateAnalytics(studentsWithStats, forestTrees, forestExercises);
      
      //Calculate leaderboards
      const leaderboardData = calculateLeaderboards(studentsWithStats);

      setStudents(studentsWithStats);
      setTrees(forestTrees);
      setExercises(forestExercises);
      setAnalytics(analyticsData);
      setLeaderboards(leaderboardData);
      setLastDataRefresh(new Date());
      
      console.log('Forest data loading complete:', {
        students: studentsWithStats.length,
        trees: forestTrees.length,
        exercises: forestExercises.length,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error loading forest data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalytics = (
    students: StudentWithStats[], 
    trees: Tree[], 
    exercises: DailyExercise[]
  ): ForestAnalytics => {
    const totalStudents = students.length;
    const totalSeedsEarned = students.reduce((sum, s) => sum + s.totalSeedsEarned, 0);
    const totalWaterEarned = students.reduce((sum, s) => sum + s.totalWaterEarned, 0);
    const totalSeedsPlanted = students.reduce((sum, s) => sum + s.totalSeedsPlanted, 0);
    const waterSpentOnOthers = students.reduce((sum, s) => sum + s.totalWaterUsedOnOthers, 0);
    const waterSpentOnSelf = totalWaterEarned - waterSpentOnOthers;
    const percentageHelpingOthers = totalWaterEarned > 0 
      ? Math.round((waterSpentOnOthers / totalWaterEarned) * 100)
      : 0;

    const totalExercisesCompleted = students.reduce((sum, s) => sum + s.exercisesCompleted, 0);
    const avgExercisesPerStudent = totalStudents > 0 ? totalExercisesCompleted / totalStudents : 0;
    const maxPossibleCompletions = totalStudents * exercises.length;
    const exerciseCompletionRate = maxPossibleCompletions > 0 
      ? Math.round((totalExercisesCompleted / maxPossibleCompletions) * 100)
      : 0;

    //Tree maturity stats
    const treeMaturityStats: { [stage: number]: number } = {};
    for (let stage = 0; stage <= 5; stage++) {
      treeMaturityStats[stage] = trees.filter(tree => tree.maturityStage === stage).length;
    }

    return {
      totalStudents,
      totalSeedsEarned,
      totalWaterEarned,
      totalSeedsPlanted,
      totalWaterSpent: waterSpentOnOthers,
      waterSpentOnOthers,
      waterSpentOnSelf,
      percentageHelpingOthers,
      avgExercisesPerStudent,
      exerciseCompletionRate,
      mostActiveDays: [],
      treeMaturityStats
    };
  };


  const calculateLeaderboards = (students: StudentWithStats[]): LeaderboardData => {
    return {
      mostWaterEarned: [...students]
        .sort((a, b) => b.totalWaterEarned - a.totalWaterEarned)
        .slice(0, 10),
      mostWaterShared: [...students]
        .sort((a, b) => b.totalWaterUsedOnOthers - a.totalWaterUsedOnOthers)
        .slice(0, 10),
      mostTreesPlanted: [...students]
        .sort((a, b) => b.totalSeedsPlanted - a.totalSeedsPlanted)
        .slice(0, 10),
      mostCollaborative: [...students]
        .sort((a, b) => {
          const aCollabRatio = a.totalWaterEarned > 0 ? a.totalWaterUsedOnOthers / a.totalWaterEarned : 0;
          const bCollabRatio = b.totalWaterEarned > 0 ? b.totalWaterUsedOnOthers / b.totalWaterEarned : 0;
          return bCollabRatio - aCollabRatio;
        })
        .slice(0, 10),
      topExerciseCompleters: [...students]
        .sort((a, b) => b.exercisesCompleted - a.exercisesCompleted)
        .slice(0, 10)
    };
  };

  const handleResourceUpdate = async (
    studentId: string, 
    resourceType: 'seeds' | 'water', 
    amount: number
  ) => {
    if (!activityManager.isActive()) {
      //ToDo: This likely will not come up, can be tested w/ removal
      alert('Please move your mouse to resume activity before making changes');
      return;
    }

    try {
      await updateStudentResources(studentId, resourceType, amount);
      
      //State
      setStudents(prev => 
        prev.map(student => 
          student.uid === studentId
            ? { ...student, [resourceType]: Math.max(0, student[resourceType] + amount) }
            : student
        )
      );
      
    } catch (error) {
      console.error('Error updating student resources:', error);
      alert('Failed to update student resources');
    }
  };


  const handleBulkResourceUpdate = async () => {
    if (!activityManager.isActive()) {
      alert('Please move your mouse to resume activity before making changes');
      return;
    }

    if (selectedStudents.size === 0) {
      alert('Please select students first');
      return;
    }

    try {
      const promises = Array.from(selectedStudents).map(studentId =>
        updateStudentResources(studentId, bulkResourceType, bulkAmount)
      );
      
      await Promise.all(promises);
      
      //State update
      setStudents(prev => 
        prev.map(student => 
          selectedStudents.has(student.uid)
            ? { ...student, [bulkResourceType]: Math.max(0, student[bulkResourceType] + bulkAmount) }
            : student
        )
      );
      
      setSelectedStudents(new Set());
      alert(`Updated ${bulkResourceType} for ${selectedStudents.size} students`);
      
    } catch (error) {
      console.error('Error bulk updating resources:', error);
      alert('Failed to update resources');
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    const newSelection = new Set(selectedStudents);
    if (newSelection.has(studentId)) {
      newSelection.delete(studentId);
    } else {
      newSelection.add(studentId);
    }
    setSelectedStudents(newSelection);
  };

  const exportData = () => {
    const data = {
      forest: forest,
      students: students,
      analytics: analytics,
      trees: trees,
      exercises: exercises,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${forest.name}-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-powder-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sage-200 border-t-sage-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-powder-600">Loading forest dashboard...</p>
          {!isInstructorActive && (
            <p className="text-sm text-orange-600 mt-2">
              Waiting for instructor activity...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.MODAL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="w-[98%] h-[95vh] bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-powder-200 bg-white">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="sm" onClick={onBack}>
                  ← Back to Dashboard
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-powder-800">
                    {forest.name}
                  </h1>
                  <p className="text-powder-600">
                    Code: <span className="font-mono font-medium">{forest.classCode}</span> • 
                    {students.length} students • 
                    Started {new Date(forest.startDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button variant="outline" size="sm" onClick={exportData}>
                  📊 Export Data
                </Button>
                <Button variant="outline" size="sm" onClick={() => console.log('Open chatbot conversations')}>
                  💬 Chatbot Conversations
                </Button>
                <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                  forest.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {forest.isActive ? 'Active' : 'Completed'}
                </div>
              </div>
            </div>

            {!isInstructorActive && (
              <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded-full animate-pulse" />
                  <span className="text-orange-800 text-sm font-medium">
                    Dashboard updates paused. Move your mouse to resume real-time monitoring.
                  </span>
                </div>
              </div>
            )}

            {/* Navigation Tabs */}
            <div className="flex space-x-2">
              {[
                { id: 'overview', label: 'Overview', count: null },
                { id: 'students', label: 'Students', count: students.length },
                { id: 'exercises', label: 'Exercises', count: exercises.length },
                { id: 'analytics', label: 'Analytics', count: null },
                { id: 'leaderboards', label: 'Leaderboards', count: null }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-sage-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && (
                    <span className="ml-2 text-xs bg-white/20 px-2 py-1 rounded">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!isInstructorActive ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Dashboard Paused</h3>
              <p className="text-gray-600">Move your mouse or click anywhere to resume monitoring</p>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && analytics && (
                <OverviewTab analytics={analytics} students={students} trees={trees} />
              )}
              
              {activeTab === 'students' && (
                <StudentsTab 
                  students={students}
                  selectedStudents={selectedStudents}
                  onToggleStudent={toggleStudentSelection}
                  onResourceUpdate={handleResourceUpdate}
                  onBulkUpdate={handleBulkResourceUpdate}
                  bulkResourceType={bulkResourceType}
                  setBulkResourceType={setBulkResourceType}
                  bulkAmount={bulkAmount}
                  setBulkAmount={setBulkAmount}
                />
              )}
              
              {activeTab === 'exercises' && (
                <ExercisesTab 
                  exercises={exercises}
                  students={students}
                  forest={forest}
                />
              )}
              
              {activeTab === 'analytics' && analytics && (
                <AnalyticsTab analytics={analytics} trees={trees} />
              )}
              
              {activeTab === 'leaderboards' && leaderboards && (
                <LeaderboardsTab leaderboards={leaderboards} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

//Tab card things
const OverviewTab: React.FC<{
  analytics: ForestAnalytics;
  students: StudentWithStats[];
  trees: Tree[];
}> = ({ analytics, students, trees }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    {/* Quick Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <div className="text-3xl font-bold text-sage-700">{analytics.totalStudents}</div>
        <div className="text-sage-600">Students</div>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <div className="text-3xl font-bold text-blue-700">{analytics.totalSeedsPlanted}</div>
        <div className="text-blue-600">Trees Planted</div>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <div className="text-3xl font-bold text-lavender-700">{analytics.exerciseCompletionRate}%</div>
        <div className="text-lavender-600">Exercise Completion</div>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <div className="text-3xl font-bold text-peach-700">{analytics.percentageHelpingOthers}%</div>
        <div className="text-peach-600">Helping Others</div>
      </div>
    </div>

    {/* Tree Growth Chart */}
    <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
      <h3 className="text-lg font-semibold text-powder-800 mb-4">Tree Growth Distribution</h3>
      <div className="grid grid-cols-6 gap-4">
        {Object.entries(analytics.treeMaturityStats).map(([stage, count]) => (
          <div key={stage} className="text-center">
            <div className="text-2xl font-bold text-green-600">{count}</div>
            <div className="text-sm text-gray-600">Stage {stage}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Recent Activity Summary */}
    <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
      <h3 className="text-lg font-semibold text-powder-800 mb-4">Forest Health Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="font-medium text-powder-700">Collaboration Score</div>
          <div className="text-2xl font-bold text-green-600">{analytics.percentageHelpingOthers}%</div>
          <div className="text-gray-600">Students helping others</div>
        </div>
        <div>
          <div className="font-medium text-powder-700">Average Progress</div>
          <div className="text-2xl font-bold text-blue-600">{Math.round(analytics.avgExercisesPerStudent)}</div>
          <div className="text-gray-600">Exercises per student</div>
        </div>
        <div>
          <div className="font-medium text-powder-700">Forest Growth</div>
          <div className="text-2xl font-bold text-sage-600">{trees.length}</div>
          <div className="text-gray-600">Total trees planted</div>
        </div>
      </div>
    </div>
  </motion.div>
);

const StudentsTab: React.FC<{
  students: StudentWithStats[];
  selectedStudents: Set<string>;
  onToggleStudent: (id: string) => void;
  onResourceUpdate: (id: string, type: 'seeds' | 'water', amount: number) => void;
  onBulkUpdate: () => void;
  bulkResourceType: 'seeds' | 'water';
  setBulkResourceType: (type: 'seeds' | 'water') => void;
  bulkAmount: number;
  setBulkAmount: (amount: number) => void;
}> = ({ 
  students, 
  selectedStudents, 
  onToggleStudent, 
  onResourceUpdate, 
  onBulkUpdate,
  bulkResourceType,
  setBulkResourceType,
  bulkAmount,
  setBulkAmount
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    {/* Bulk Operations */}
    <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
      <h3 className="text-lg font-semibold text-powder-800 mb-4">Bulk Operations</h3>
      <div className="flex items-center space-x-4">
        <select
          value={bulkResourceType}
          onChange={(e) => setBulkResourceType(e.target.value as 'seeds' | 'water')}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="seeds">Seeds</option>
          <option value="water">Water</option>
        </select>
        <input
          type="number"
          value={bulkAmount}
          onChange={(e) => setBulkAmount(parseInt(e.target.value) || 1)}
          className="px-3 py-2 border rounded-lg w-20"
          min="1"
        />
        <Button 
          variant="primary" 
          onClick={onBulkUpdate}
          disabled={selectedStudents.size === 0}
        >
          Update {selectedStudents.size} Selected Students
        </Button>
        <Button 
          variant="outline" 
          onClick={() => onToggleStudent('')}
        >
          Clear Selection
        </Button>
      </div>
    </div>

    {/* Student List */}
    <div className="bg-white rounded-xl shadow-lg border border-powder-200 overflow-hidden">
      <div className="p-4 bg-gray-50 border-b">
        <h3 className="text-lg font-semibold text-powder-800">Student Roster ({students.length})</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Select</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Student</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Seeds</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Water</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Trees</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Exercises</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Water Shared</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {students.map((student) => (
              <tr key={student.uid} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedStudents.has(student.uid)}
                    onChange={() => onToggleStudent(student.uid)}
                    className="rounded"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-sage-200 rounded-full flex items-center justify-center text-sm font-medium">
                      {student.initials}
                    </div>
                    <div>
                      <div className="font-medium text-powder-800">
                        {student.firstName} {student.lastName}
                      </div>
                      <div className="text-sm text-gray-600">@{student.username}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{student.seeds}</span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => onResourceUpdate(student.uid, 'seeds', 1)}
                        className="w-6 h-6 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                      >
                        +
                      </button>
                      <button
                        onClick={() => onResourceUpdate(student.uid, 'seeds', -1)}
                        className="w-6 h-6 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                      >
                        -
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{student.water}</span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => onResourceUpdate(student.uid, 'water', 1)}
                        className="w-6 h-6 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                      >
                        +
                      </button>
                      <button
                        onClick={() => onResourceUpdate(student.uid, 'water', -1)}
                        className="w-6 h-6 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                      >
                        -
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-medium">{student.treesPlanted}</td>
                <td className="px-4 py-3 font-medium">{student.exercisesCompleted}</td>
                <td className="px-4 py-3 font-medium text-green-600">{student.totalWaterUsedOnOthers}</td>
                <td className="px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => console.log('View student dashboard:', student.uid)}
                  >
                    View Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </motion.div>
);

const ExercisesTab: React.FC<{
  exercises: DailyExercise[];
  students: StudentWithStats[];
  forest: Forest;
}> = ({ exercises, students, forest }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    <div className="bg-white rounded-xl shadow-lg border border-powder-200 overflow-hidden">
      <div className="p-4 bg-gray-50 border-b">
        <h3 className="text-lg font-semibold text-powder-800">Exercise Completion Matrix</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 sticky left-0 bg-gray-50">
                Student
              </th>
              {exercises.map((exercise) => (
                <th key={exercise.id} className="px-3 py-3 text-center text-sm font-medium text-gray-600 min-w-[80px]">
                  Day {exercise.day}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {students.map((student) => (
              <tr key={student.uid} className="hover:bg-gray-50">
                <td className="px-4 py-3 sticky left-0 bg-white">
                  <div className="font-medium text-powder-800">
                    {student.firstName} {student.lastName}
                  </div>
                </td>
                {exercises.map((exercise) => {
                  const hasCompleted = student.exerciseCompletions.some(
                    comp => comp.exerciseDay === exercise.day
                  );
                  return (
                    <td key={exercise.id} className="px-3 py-3 text-center">
                      <div className={`w-6 h-6 rounded-full mx-auto ${
                        hasCompleted ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                        {hasCompleted && (
                          <svg className="w-4 h-4 text-white mx-auto mt-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="px-4 py-3 text-center font-medium">
                  {student.exercisesCompleted}/{exercises.length}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </motion.div>
);

const AnalyticsTab: React.FC<{
  analytics: ForestAnalytics;
  trees: Tree[];
}> = ({ analytics, trees }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Engagement Analytics */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">Engagement Analytics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Exercise Completion Rate</span>
            <span className="font-bold text-lg">{analytics.exerciseCompletionRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${analytics.exerciseCompletionRate}%` }}
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Collaboration Rate</span>
            <span className="font-bold text-lg">{analytics.percentageHelpingOthers}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${analytics.percentageHelpingOthers}%` }}
            />
          </div>
        </div>
      </div>

      {/* Resource Distribution */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">Resource Distribution</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Seeds Earned</span>
              <span className="font-bold">{analytics.totalSeedsEarned}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Seeds Planted</span>
              <span className="font-bold">{analytics.totalSeedsPlanted}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Water Earned</span>
              <span className="font-bold">{analytics.totalWaterEarned}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Water Shared</span>
              <span className="font-bold text-green-600">{analytics.waterSpentOnOthers}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Tree Growth Analytics */}
    <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
      <h3 className="text-lg font-semibold text-powder-800 mb-4">Tree Growth Analytics</h3>
      <div className="grid grid-cols-6 gap-4">
        {Object.entries(analytics.treeMaturityStats).map(([stage, count]) => (
          <div key={stage} className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{count}</div>
            <div className="text-sm text-gray-600">Stage {stage}</div>
            <div className="text-xs text-gray-500">
              {stage === '0' && 'Seed'}
              {stage === '1' && 'Sprout'}
              {stage === '2' && 'Seedling'}
              {stage === '3' && 'Sapling'}
              {stage === '4' && 'Young'}
              {stage === '5' && 'Mature'}
            </div>
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);

const LeaderboardsTab: React.FC<{
  leaderboards: LeaderboardData;
}> = ({ leaderboards }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top Water Earners */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">🏆 Top Water Earners</h3>
        <div className="space-y-3">
          {leaderboards.mostWaterEarned.slice(0, 5).map((student, index) => (
            <div key={student.uid} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  index === 0 ? 'bg-yellow-500' :
                  index === 1 ? 'bg-gray-400' :
                  index === 2 ? 'bg-orange-500' :
                  'bg-gray-300'
                }`}>
                  {index + 1}
                </div>
                <span className="font-medium">{student.firstName} {student.lastName}</span>
              </div>
              <span className="text-blue-600 font-bold">{student.totalWaterEarned}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Most Collaborative */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">🤝 Most Collaborative</h3>
        <div className="space-y-3">
          {leaderboards.mostCollaborative.slice(0, 5).map((student, index) => (
            <div key={student.uid} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  index === 0 ? 'bg-green-500' :
                  index === 1 ? 'bg-blue-500' :
                  index === 2 ? 'bg-purple-500' :
                  'bg-gray-300'
                }`}>
                  {index + 1}
                </div>
                <span className="font-medium">{student.firstName} {student.lastName}</span>
              </div>
              <span className="text-green-600 font-bold">
                {student.totalWaterEarned > 0 
                  ? Math.round((student.totalWaterUsedOnOthers / student.totalWaterEarned) * 100)
                  : 0}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Tree Planters */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">🌱 Top Tree Planters</h3>
        <div className="space-y-3">
          {leaderboards.mostTreesPlanted.slice(0, 5).map((student, index) => (
            <div key={student.uid} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  index === 0 ? 'bg-green-600' :
                  index === 1 ? 'bg-green-500' :
                  index === 2 ? 'bg-green-400' :
                  'bg-gray-300'
                }`}>
                  {index + 1}
                </div>
                <span className="font-medium">{student.firstName} {student.lastName}</span>
              </div>
              <span className="text-green-700 font-bold">{student.totalSeedsPlanted}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Exercise Completers */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-powder-200">
        <h3 className="text-lg font-semibold text-powder-800 mb-4">📚 Top Exercise Completers</h3>
        <div className="space-y-3">
          {leaderboards.topExerciseCompleters.slice(0, 5).map((student, index) => (
            <div key={student.uid} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  index === 0 ? 'bg-purple-600' :
                  index === 1 ? 'bg-purple-500' :
                  index === 2 ? 'bg-purple-400' :
                  'bg-gray-300'
                }`}>
                  {index + 1}
                </div>
                <span className="font-medium">{student.firstName} {student.lastName}</span>
              </div>
              <span className="text-purple-600 font-bold">{student.exercisesCompleted}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

export default ForestDashboardView;